import { getAddonPrice } from "./quiz-results-addons.js";
import { getQuizResultsTexts } from "./quiz-results-texts.js";

let isTrackingConfigured = false;

/**
 * @returns {((...args: any[]) => void) | null}
 */
function getGtag() {
  const gtag = /** @type {any} */ (window).gtag;
  return typeof gtag === "function" ? gtag : null;
}

/**
 * @param {any} value
 * @returns {number}
 */
function roundToTwo(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/**
 * @param {string | number} value
 * @returns {string}
 */
function formatPrice(value) {
  const num = roundToTwo(value);
  return `$${num % 1 === 0 ? num : num.toFixed(2)}`;
}

/**
 * @returns {any}
 */
function getCustomerFromWindow() {
  return (
    /** @type {any} */ (window).shopify_customer ||
    /** @type {any} */ (window).obscure_customer ||
    null
  );
}

/**
 * @param {any} customer
 * @returns {boolean}
 */
function isFreshAccount(customer) {
  if (!customer) return true;

  const createdAt = customer.createdAt || customer.created_at;
  if (!createdAt) return true;

  const created = new Date(createdAt);
  if (Number.isNaN(Number(created))) return true;

  const now = new Date();
  const dayInMilliseconds = 24 * 60 * 60 * 1000;
  const accountAgeInDays = (Number(now) - Number(created)) / dayInMilliseconds;
  return accountAgeInDays < 1;
}

/**
 * @param {any} regimenProduct
 * @param {any} discount
 * @param {string} itemListName
 * @returns {any}
 */
function generateRegimenItem(regimenProduct, discount, itemListName) {
  const texts = getQuizResultsTexts();
  const setPrice = Number(regimenProduct?.variant?.price || 0);
  const discountValue = Number(discount?.value || 0);
  const discountedPrice = setPrice * (1 - discountValue / 100);
  const discountAmount = roundToTwo(setPrice - discountedPrice);
  const regimenNumber = String(regimenProduct?.title || "").match(/\d+/)?.[0];
  const itemName = regimenNumber
    ? `${texts.tracking.regimenNameTemplatePrefix}${regimenNumber}${texts.tracking.regimenNameTemplateSuffix}`
    : texts.tracking.regimenDefaultName;

  return {
    index: 0,
    item_name: itemName,
    item_variant: texts.tracking.classicSet,
    item_id: regimenProduct?.variant?.sku || "",
    coupon: discount?.code,
    discount: discountAmount,
    affiliation: texts.tracking.onlineStore,
    item_brand: texts.tracking.geologie,
    item_category: texts.tracking.skincare,
    item_category2: texts.tracking.sampleSetCategory,
    item_category3: texts.tracking.classicCategory,
    item_list_id: texts.tracking.itemListId,
    item_list_name: itemListName,
    price: discountedPrice,
    quantity: 1,
  };
}

/**
 * @param {any} addon
 * @param {number} index
 * @param {string} itemListName
 * @param {boolean} isOtpOn
 * @returns {any}
 */
function generateAddonItem(addon, index, itemListName, isOtpOn) {
  const texts = getQuizResultsTexts();
  const fullPrice = Number(addon?.variant?.price || 0);
  const discountedPrice = getAddonPrice(addon?.variant, isOtpOn);
  const discountAmount = roundToTwo(fullPrice - discountedPrice);

  return {
    index,
    item_name: addon?.title || addon?.variant?.title || "",
    item_variant: texts.tracking.addonVariant,
    item_id: addon?.variant?.sku || "",
    coupon: texts.tracking.addonCoupon,
    discount: discountAmount,
    affiliation: texts.tracking.onlineStore,
    item_brand: texts.tracking.geologie,
    item_category: texts.tracking.skincare,
    item_category2: texts.tracking.trialAddonCategory,
    item_list_id: texts.tracking.itemListId,
    item_list_name: itemListName,
    price: discountedPrice,
    quantity: 1,
  };
}

export function initResultsTrackingConfig() {
  if (isTrackingConfigured) return;
  const gtag = getGtag();
  if (!gtag) return;

  gtag("config", "UA-118463961-4", {
    page_title: document.title,
    page_path: "/pages/quiz-results",
    custom_map: {
      dimension7: "has_shopify_profile",
      dimension11: "recommended_set",
      dimension12: "price",
      dimension14: "gender",
      dimension15: "budget",
      dimension16: "age",
    },
  });

  isTrackingConfigured = true;
}

/**
 * @param {any} discountWithRefills
 */
export function trackDiscountView(discountWithRefills) {
  const gtag = getGtag();
  if (!gtag) return;

  const isDefaultDiscount = Number(discountWithRefills?.value || 0) === 5;
  const eventName = isDefaultDiscount
    ? "default_results_page_view"
    : "custom_results_page_view";

  gtag("event", eventName, {
    page_location: window.location.href,
    page_path: window.location.pathname + window.location.search + window.location.hash,
    page_title: document.title,
  });
}

/**
 * @param {{ regimenProduct: any, discount: any }} input
 */
export function trackResultsView(input) {
  const gtag = getGtag();
  if (!gtag) return;

  const regimenProduct = input?.regimenProduct;
  if (!regimenProduct) return;

  const texts = getQuizResultsTexts();
  const discount = input?.discount;
  const customer = getCustomerFromWindow();
  const hasShopifyProfile = (!isFreshAccount(customer)).toString();
  const setPrice = Number(regimenProduct?.variant?.price || 0);
  const discountValue = Number(discount?.value || 0);
  const discountedPrice = formatPrice(setPrice * (1 - discountValue / 100));

  gtag("event", "view_item_list", {
    item_list_id: texts.tracking.itemListId,
    item_list_name: texts.tracking.recommendedSetListName,
    recommended_set: texts.tracking.classicCategory,
    has_shopify_profile: hasShopifyProfile,
    gender: texts.tracking.unknown,
    budget: texts.tracking.unknown,
    age: texts.tracking.unknown,
    price: discountedPrice,
    items: [
      generateRegimenItem(regimenProduct, discount, texts.tracking.recommendedSetListName),
    ],
  });
}

/**
 * @param {{ selectedAddons: any[], isAutoSelected: boolean, isOtpOn: boolean }} input
 */
export function trackAddonSelection(input) {
  const gtag = getGtag();
  if (!gtag) return;

  const texts = getQuizResultsTexts();
  const selectedAddons = Array.isArray(input?.selectedAddons) ? input.selectedAddons : [];
  let addOnSelection = "none";
  if (selectedAddons.length === 1) addOnSelection = "single";
  if (selectedAddons.length > 1) addOnSelection = "multiple";

  gtag("event", "select_item", {
    item_list_id: texts.tracking.itemListId,
    item_list_name: texts.tracking.addonProductsListName,
    add_on_selection: addOnSelection,
    add_on_selection_type: input?.isAutoSelected ? "auto" : "manual",
    items: selectedAddons.map((addon, index) =>
      generateAddonItem(
        addon,
        index,
        texts.tracking.addonProductsListName,
        Boolean(input?.isOtpOn),
      ),
    ),
  });
}

/**
 * @param {{ regimenProduct: any, selectedAddons: any[], discount: any, isOtpOn: boolean }} input
 */
export function trackCheckout(input) {
  const gtag = getGtag();
  if (!gtag) return;

  const texts = getQuizResultsTexts();
  const regimenProduct = input?.regimenProduct;
  if (!regimenProduct) return;

  const selectedAddons = Array.isArray(input?.selectedAddons) ? input.selectedAddons : [];
  const discount = input?.discount;
  const isOtpOn = Boolean(input?.isOtpOn);

  const regimenItem = generateRegimenItem(
    regimenProduct,
    discount,
    texts.tracking.productsAddedToCartListName,
  );
  const addonItems = selectedAddons.map((addon, index) =>
    generateAddonItem(
      addon,
      index + 1,
      texts.tracking.productsAddedToCartListName,
      isOtpOn,
    ),
  );
  const items = addonItems.concat(regimenItem);

  const value = selectedAddons.reduce(
    (sum, addon) => sum + Number(addon?.variant?.price || 0),
    Number(regimenProduct?.variant?.price || 0),
  );

  gtag("event", "add_to_cart", {
    currency: texts.tracking.currency,
    value,
    items,
  });

  gtag("event", "begin_checkout", {
    currency: texts.tracking.currency,
    value,
    coupon: discount?.code,
    items,
  });
}
